import React, {useRef} from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter, CircularProgress, Typography ,Box} from "@mui/material";
import { format } from "date-fns"; // Import date-fns for safe formatting

// Define the exact column structure based ONLY on the provided JSON fields
const SALESLIST_CASH_COLUMNS = [
    { header: 'DEPT NO', key: 'DEPTNO', numeric: false },
    { header: 'SR NO', key: 'SR_NO', numeric: false }, 
    { header: 'INV NO', key: 'INVOICE_NO', numeric: false }, // Mapped from INVOICE_NO
    { header: 'DATE', key: 'SR_DATE', isDate: true, numeric: false }, // Mapped from SR_DATE
    { header: 'CUSTOMER', key: 'CUSTOMER', numeric: false },
    { header: 'LPO', key: 'LPO', numeric: false },
    { header: 'SALES ID', key: 'SALESPERSON_ID', numeric: false }, // New field
    { header: 'SALES MAN', key: 'SALES_PERSON', numeric: false, isLabel: true }, // Mapped from SALES_PERSON (and used for total label)
    { header: 'ACCOUNT', key: 'ACCOUNT', numeric: false },
    { header: 'AMOUNT', key: 'AMOUNT', numeric: true },
    { header: 'VAT AMT', key: 'VATAMT', numeric: true },
    { header: 'NET RETURN', key: 'NET RETURN', numeric: true }, // New field, numeric
    { header: 'COST', key: 'COST', numeric: true }, // New field, numeric
    { header: 'TYPE', key: 'TYPE', numeric: false },
];
const TOTAL_COLUMNS = SALESLIST_CASH_COLUMNS.length; // Recalculated total columns

// Assuming formatNumber helper is passed down or available
const formatNumber = (val) => Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SalesTableFlatCASHReturn = ({ rows, grandColumnTotals, loading,loadNextPage, hasMore }) => {

    const tableContainerRef = useRef(null);
    
    // Helper function for safe date formatting
    const safeFormatDate = (dateValue) => {
        if (!dateValue) return '-';
        try {
            const date = new Date(dateValue);
            // Check if the date is a valid object and not NaN (Invalid Date)
            if (date instanceof Date && !isNaN(date.getTime())) {
                return format(date, "dd-MM-yyyy");
            }
        } catch (e) {
            console.error("Error formatting date:", e);
        }
        return String(dateValue).split('T')[0] || '-'; // Fallback to raw date part if safe format fails
    };

    // Helper function to robustly get the cell value
    const getCellValue = (row, col) => {
        // Directly access the key. This handles standard keys (DEPTNO) and keys with spaces (NET RETURN)
        // Note: For keys with spaces like 'NET RETURN', standard bracket notation `row['NET RETURN']` is required, which works in JavaScript.
        if (row[col.key] !== undefined && row[col.key] !== null) {
            return row[col.key];
        }
        return null;
    };


    // 🚨 2. Define the scroll detection logic
    const handleTableScroll = () => {

        const currentTarget = tableContainerRef.current;

        if (!currentTarget || loading || !hasMore || rows.length === 0) return;

        const { scrollTop, scrollHeight, clientHeight } = currentTarget;

        // Load the next page when reaching the bottom (with a 10px buffer)
        if (scrollHeight - scrollTop <= clientHeight + 10) {
            loadNextPage();
        }
    };


    const renderBody = () => (
        <TableBody>
            {console.log("rows -> Salestableflatcash", rows)}
            {rows.map((row, index) => (
                <TableRow key={`row-cash-${index}`} sx={{ backgroundColor: '#fff', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                    {SALESLIST_CASH_COLUMNS.map(col => {
                        // Use the robust helper function to get the correct cell value
                        const cellValue = getCellValue(row, col);

                        let content = cellValue;
                        
                        if (col.numeric) {
                            // SR_NO is treated as numeric data, but we don't want the currency format on it.
                            if (col.key === 'SR_NO') {
                                content = cellValue;
                            } else {
                                content = formatNumber(cellValue);
                            }
                        } else if (col.isDate) {
                            // Use the safe date formatter for date columns
                            content = safeFormatDate(cellValue);
                        } 

                        return (
                            <TableCell
                                key={col.key}
                                align={col.numeric ? "right" : "left"}
                                sx={col.key === 'AMOUNT' ? { fontWeight: 'bold' } : {}}
                            >
                                {content}
                            </TableCell>
                        );
                    })}
                </TableRow>
            ))}

             {/* Load More Indicator must be wrapped in a TableRow/TableCell if inside TableBody */}
             {hasMore && (
                        <TableRow>
                            <TableCell colSpan={TOTAL_COLUMNS} align="center">
                                <Box sx={{ py: 2 }}>
                                    <CircularProgress size={24} />
                                    <Typography variant="caption" display="block">
                                        Loading more data...
                                    </Typography>
                                </Box>
                            </TableCell>
                        </TableRow>
                    )}

            {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={TOTAL_COLUMNS} align="center"><Typography variant="subtitle1" color="textSecondary">No sales data found for the selected filters.</Typography></TableCell></TableRow>
            )}
        </TableBody>
    );

    const renderFooter = () => {
        // Keys to total in the footer (Updated based on new schema)
        const TOTAL_KEYS = ['AMOUNT', 'VATAMT', 'NET RETURN', 'COST'];
        
        return (
        <TableFooter>
            <TableRow sx={{ backgroundColor: '#b3b3b3', borderTop: '3px double #000' }}>
                {SALESLIST_CASH_COLUMNS.map(col => {
                    const isTotalKey = TOTAL_KEYS.includes(col.key);
                    const isLabelCell = col.isLabel;
                    
                    // Determine content based on whether it's the label or a total value
                    let content;
                    if (isLabelCell) {
                        content = 'GRAND TOTAL:';
                    } else if (isTotalKey) {
                        content = formatNumber(grandColumnTotals[col.key] || 0);
                    } else {
                        content = ''; // Blank cell for non-total, non-label columns
                    }
                    
                    let align = isTotalKey || isLabelCell ? 'right' : 'left';
                    let cellStyle = { fontWeight: 'bold', color: '#000000', fontSize: '1.1em' }; // Use a slightly larger size

                    return (
                        <TableCell
                            key={col.key}
                            align={align}
                            sx={{
                                ...cellStyle,
                                position: 'sticky',
                                bottom: 0,
                                zIndex: 10,
                                backgroundColor: '#b3b3b3',
                                padding: '8px 16px',
                                whiteSpace:"nowrap"
                            }}
                        >
                            {content}
                        </TableCell>
                    );
                })}
            </TableRow>
        </TableFooter>
    )};

    return (
        <TableContainer
            ref={tableContainerRef}
            onScroll={handleTableScroll} 
            sx={{
                maxHeight: 'calc(100vh - 350px)',
                overflowY: 'auto', width: '100%'
            }}>
            <Table stickyHeader aria-label="sales analysis cash report">
                <TableHead>
                    <TableRow>
                        {SALESLIST_CASH_COLUMNS.map(col => (
                            <TableCell key={col.key} align={col.numeric ? "right" : "left"} style={{ minWidth: 120, whiteSpace: 'nowrap' }}>
                                {col.header}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                {renderBody()}
                {/* Ensure grandColumnTotals check is robust */}
                {grandColumnTotals && Object.keys(grandColumnTotals).length > 0 && renderFooter()}
            </Table>

            {loading && (
                <div style={{ display: "flex", justifyContent: 'center', alignItems: "center", width: "100%", height: "100%", position: "absolute", zIndex: 100, backgroundColor: "#ffffff80", top: "0px", left: "0px" }}>
                    <CircularProgress size={48} />
                </div>
            )}

        </TableContainer>
    );
};

export default SalesTableFlatCASHReturn;